import React, { useState } from 'react';
import { OptimizedServerCard } from './OptimizedServerCard';
import { useServers } from '@/hooks/useServers';
import { useGame } from '@/contexts/GameContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Loader2, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
interface ServerListProps {
  sortBy?: 'default' | 'votes' | 'popular' | 'recent' | 'clicks' | 'competitive';
  selectedTags?: string[];
  gameFilter?: string;
  prestigeFilter?: 'all' | 'prestige-only' | 'regular-only';
  isCompetitiveMode?: boolean;
}
export const ServerList: React.FC<ServerListProps> = ({
  sortBy = 'default',
  selectedTags = [],
  gameFilter,
  prestigeFilter = 'all',
  isCompetitiveMode = false
}) => {
  const {
    t
  } = useLanguage();
  const {
    selectedGame
  } = useGame();
  const [currentPage, setCurrentPage] = useState(1);
  const serversPerPage = 25;
  const {
    data,
    isLoading,
    error
  } = useServers(gameFilter || selectedGame?.id, currentPage, serversPerPage, sortBy, selectedTags.length > 0 ? selectedTags : undefined, prestigeFilter, isCompetitiveMode);

  // Reset page when game selection, sort, or tags change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [selectedGame, sortBy, selectedTags, prestigeFilter, isCompetitiveMode]);
  if (isLoading) {
    return <section className="py-8" id="server-list">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-gaming-primary" />
            <span className="ml-2 font-mono text-slate-400">{t('common.loadingServers')}</span>
          </div>
        </div>
      </section>;
  }
  if (error) {
    return <section className="py-8" id="server-list">
        <div className="container mx-auto px-4">
          <div className="text-center py-12">
            <p className="font-mono text-gaming-danger mb-4">{t('common.errorLoadingServers')}</p>
          </div>
        </div>
      </section>;
  }
  const getTitle = () => {
    let title = t('index.topServers');
    if (selectedGame) {
      title += ` ${selectedGame.name.toUpperCase()}`;
    }
    if (selectedTags.length > 0) {
      title += ` - ${selectedTags.join(', ').toUpperCase()}`;
    }
    return title;
  };

  // Séparer les serveurs Prestige des autres - vérification stricte du statut
  const servers = data?.servers || [];

  // Vérifier strictement si un serveur est Prestige (sponsored + date valide)
  const isServerPrestige = (server: any) => {
    return server.is_sponsored && server.prestige_expires_at && new Date(server.prestige_expires_at) > new Date();
  };
  const prestigeGoldServers = servers.filter(server => isServerPrestige(server) && (server.sponsor_tier === 'gold' || server.sponsor_tier === 'premium' || server.sponsor_tier === 'PRESTIGE GOLD'));
  const regularServers = servers.filter(server => !isServerPrestige(server) || server.sponsor_tier !== 'gold' && server.sponsor_tier !== 'premium' && server.sponsor_tier !== 'PRESTIGE GOLD');
  const totalPages = data?.totalPages || 1;
  const currentPageNumber = data?.currentPage || 1;
  const scrollToServerList = () => {
    // Scroll vers le top de la page pour assurer que l'utilisateur voit le début du contenu
    const yOffset = -100; // Décalage pour tenir compte du header fixe
    const element = document.getElementById('server-list');
    if (element) {
      const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: Math.max(0, y), behavior: 'smooth' });
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };
  const handlePreviousPage = () => {
    if (currentPageNumber > 1) {
      setCurrentPage(currentPageNumber - 1);
      scrollToServerList();
    }
  };
  const handleNextPage = () => {
    if (currentPageNumber < totalPages) {
      setCurrentPage(currentPageNumber + 1);
      scrollToServerList();
    }
  };
  const handlePageClick = (page: number) => {
    setCurrentPage(page);
    scrollToServerList();
  };
  const getVisiblePages = () => {
    const pages = [];
    // Mobile: max 3 pages visibles, Desktop: max 5 pages
    const maxVisiblePages = window.innerWidth < 640 ? 3 : 5;
    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      const halfVisible = Math.floor(maxVisiblePages / 2);
      if (currentPageNumber <= halfVisible + 1) {
        for (let i = 1; i <= maxVisiblePages; i++) {
          pages.push(i);
        }
      } else if (currentPageNumber >= totalPages - halfVisible) {
        for (let i = totalPages - maxVisiblePages + 1; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        for (let i = currentPageNumber - halfVisible; i <= currentPageNumber + halfVisible; i++) {
          pages.push(i);
        }
      }
    }
    return pages;
  };
  return <section id="server-list" className="py-0">
      <div className="container mx-auto px-2 sm:px-4">
        <div className="mb-8 text-center">
          
          
        </div>

        <div className="space-y-3">
          {servers.length > 0 ? <>
              {isCompetitiveMode ?
          // Mode compétitif : affichage classement événement
          servers.map((server, index) => <OptimizedServerCard key={server.id} rank={(server as any).competitive_rank || index + 1} name={server.name} description={server.description || ''} ip={server.ip_address} version={server.version || 'N/A'} category={server.games.name} players={{
            online: server.online_players || 0,
            max: server.max_players || 100
          }} votes={server.total_votes || 0} tags={server.tags || []} isOnline={server.is_online || false} serverId={server.id} serverSlug={server.slug} isSponsored={isServerPrestige(server)} sponsorTier={server.sponsor_tier} customColor={server.custom_color} bannerImageUrl={server.banner_image_url} logoImageUrl={server.logo_image_url} prestigeExpiresAt={server.prestige_expires_at} isCompetitiveMode={true} competitiveVotes={(server as any).competitive_votes} competitiveRank={(server as any).competitive_rank} rankChange={(server as any).rank_change} />) : <>
                  {/* Mode normal : Serveurs Prestige sans numéro de rang */}
                  {prestigeGoldServers.map(server => <OptimizedServerCard key={server.id} rank={0} name={server.name} description={server.description || ''} ip={server.ip_address} version={server.version || 'N/A'} category={server.games.name} players={{
              online: server.online_players || 0,
              max: server.max_players || 100
            }} votes={server.total_votes || 0} tags={server.tags || []} isOnline={server.is_online || false} serverId={server.id} serverSlug={server.slug} isSponsored={isServerPrestige(server)} sponsorTier={server.sponsor_tier} customColor={server.custom_color} bannerImageUrl={server.banner_image_url} logoImageUrl={server.logo_image_url} prestigeExpiresAt={server.prestige_expires_at} rankChange={(server as any).rank_change} />)}
                  
                  {/* Serveurs réguliers avec numérotation basée sur la page */}
                  {regularServers.map((server, index) => <OptimizedServerCard key={server.id} rank={(currentPageNumber - 1) * serversPerPage + index + 1} name={server.name} description={server.description || ''} ip={server.ip_address} version={server.version || 'N/A'} category={server.games.name} players={{
              online: server.online_players || 0,
              max: server.max_players || 100
            }} votes={server.total_votes || 0} tags={server.tags || []} isOnline={server.is_online || false} serverId={server.id} serverSlug={server.slug} isSponsored={isServerPrestige(server)} sponsorTier={server.sponsor_tier} customColor={server.custom_color} bannerImageUrl={server.banner_image_url} logoImageUrl={server.logo_image_url} prestigeExpiresAt={server.prestige_expires_at} rankChange={(server as any).rank_change} />)}
                </>}
            </> : <div className="text-center py-12">
              <p className="font-mono text-slate-400 mb-4">
                {selectedGame || selectedTags.length > 0 ? t('common.noServersWithFilters') : t('common.noServersFound')}
              </p>
            </div>}
        </div>

        {/* Pagination */}
        {totalPages > 1 && <div className="flex justify-center items-center mt-8 space-x-1.5 sm:space-x-2">
            <Button variant="outline" size="default" onClick={handlePreviousPage} disabled={currentPageNumber === 1} className="border-slate-600 text-slate-300 hover:text-white hover:bg-slate-700 disabled:opacity-50 h-10 px-3 sm:px-4">
              <ChevronLeft className="w-4 h-4" />
              <span className="hidden sm:inline ml-1">Précédent</span>
            </Button>

            <div className="flex space-x-1">
              {getVisiblePages().map(page => <Button key={page} variant={page === currentPageNumber ? "default" : "outline"} size="default" onClick={() => handlePageClick(page)} className={`min-w-[2.75rem] h-10 ${page === currentPageNumber ? 'bg-gaming-primary text-white' : 'border-slate-600 text-slate-300 hover:text-white hover:bg-slate-700'}`}>
                  {page}
                </Button>)}
            </div>

            <Button variant="outline" size="default" onClick={handleNextPage} disabled={currentPageNumber === totalPages} className="border-slate-600 text-slate-300 hover:text-white hover:bg-slate-700 disabled:opacity-50 h-10 px-3 sm:px-4">
              <span className="hidden sm:inline mr-1">Suivant</span>
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>}

    {/* Info pagination stylisée */}
    {data && <div className="mt-4 text-center text-sm text-muted-foreground">
        {t('serverList.page', {
          current: currentPageNumber.toString(),
          total: totalPages.toString()
        })}
        {data.totalCount > 0 && <span>
            {' '}
            {t('serverList.display', {
            from: Math.min((currentPageNumber - 1) * serversPerPage + 1, data.totalCount).toString(),
            to: Math.min(currentPageNumber * serversPerPage, data.totalCount).toString(),
            count: data.totalCount.toString()
          })}
          </span>}
      </div>}
      </div>
    </section>;
};